import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ButtonType } from '@src/components/Button'
import { AlertModal } from '@src/components/Modal/AlertModal'
import { useBasketItems } from '@src/hooks/useBasketItems/useBasketItems'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { BasketItemWithPrices } from '@src/hooks/useTotals/useBasketTotals'
import { useBasketTotalsContext } from '@src/hooks/useTotals/BasketTotalsContext'

import { BasketItem as BasketItemComponent } from './BasketItem'
import { Container } from './BasketItems.styles'

export const BasketItems: React.FC = () => {
  const { t } = useTranslation('checkout')
  const marketplace = useMarketplace()
  const basketItems = useBasketItems()
  const { basketTotals } = useBasketTotalsContext()
  // modal needs a separate state cause the item get removed before the modal is closed
  const [showModal, setShowModal] = useState(false)

  const [modalData, setModalData] = useState<null | {
    title: string
    subtitle: string
    onModalCloseAfterConfirmAction: () => void
  }>(null)

  const basketTotalsData = basketTotals.data

  const allowSingleItemOrderNotes = !!marketplace.allowSingleItemOrderNotes

  // Calculate effective max quantities for add-on items based on group limits
  const getEffectiveMaxQuantity = useCallback(
    (basketItemWithPrices: BasketItemWithPrices) => {
      const { outletMenuItem } = basketItemWithPrices

      // If not an add-on menu item, use original maxPurchaseQuantity
      if (!outletMenuItem.parentMenu?.addOnItemsMenu) {
        return outletMenuItem.maxPurchaseQuantity
      }

      const groupMaxQuantity = outletMenuItem.parentMenu.addOnItemsMaxQuantity
      const itemMaxQuantity = outletMenuItem.maxPurchaseQuantity

      if (groupMaxQuantity === null) return itemMaxQuantity

      // Count total quantity of all items from the same add-on group
      const totalGroupQuantity = basketTotalsData.basketItemsWithPrices
        .filter(
          item =>
            item.outletMenuItem.parentMenu?.id ===
              outletMenuItem.parentMenu?.id &&
            item.basketItem.id !== basketItemWithPrices.basketItem.id // Exclude current item
        )
        .reduce((sum, item) => sum + item.basketItem.quantity, 0)

      const groupRemainingQuantity = Math.max(
        0,
        groupMaxQuantity - totalGroupQuantity
      )

      // Return the most restrictive limit between the group remaining and the item's own max
      if (itemMaxQuantity === null) {
        return groupRemainingQuantity
      }
      return Math.min(groupRemainingQuantity, itemMaxQuantity)
    },
    [basketTotalsData]
  )

  // function to update the quantity of a basket item
  const curriedUpdateBasketItemQuantity =
    (basketItemWithPrices: BasketItemWithPrices) => (newQuantity: number) => {
      // if it's an add-on item or the quantity is being increased, we don't need to do anything special
      if (
        basketItemWithPrices.isAddOnItem ||
        newQuantity >= basketItemWithPrices.basketItem.quantity
      ) {
        return basketItems.updateQuantity({
          basketItemId: basketItemWithPrices.basketItem.id,
          quantity: newQuantity,
        })
      }

      // if the item is not an add-on item, we need to check if the modal should be shown. Which is either:
      // * the quantity is being set to 0
      // * the quantity is being set to a value that would take the total below the threshold (if there is one)
      const basketTotalReductionAmount =
        basketItemWithPrices.totalPriceForOneItem *
        (basketItemWithPrices.basketItem.quantity - newQuantity)

      const affectedAddOnGroups =
        basketTotalsData.checkoutBasketTotals?.addOnItemsTotals.filter(
          addOnGroup =>
            addOnGroup?.amountOverAddOnThreshold &&
            basketTotalReductionAmount > addOnGroup.amountOverAddOnThreshold &&
            basketItems.addOnItems.some(
              item => item.parentMenuId === addOnGroup.menuGroupId
            )
        ) || []

      const isAddOnItemRemovalRequired = affectedAddOnGroups.length > 0

      if (newQuantity === 0 || isAddOnItemRemovalRequired) {
        setShowModal(true)
        setModalData({
          title: t(
            isAddOnItemRemovalRequired
              ? 'remove_item_and_add_on_items'
              : 'remove_item'
          ),
          subtitle: isAddOnItemRemovalRequired
            ? t('remove_item_and_add_on_items_desc')
            : t('remove_item_desc', {
                itemName: basketItemWithPrices.basketItem.name,
              }),
          onModalCloseAfterConfirmAction: () => {
            basketItems.updateQuantities({
              basketItemUpdates: [
                {
                  basketItemId: basketItemWithPrices.basketItem.id,
                  quantity: newQuantity,
                },
                ...(isAddOnItemRemovalRequired
                  ? basketItems.addOnItems
                      .filter(item =>
                        affectedAddOnGroups
                          .map(addOnGroup => addOnGroup?.menuGroupId)
                          .includes(item.parentMenuId)
                      )
                      .map(item => ({
                        basketItemId: item.id,
                        quantity: 0,
                      }))
                  : []),
              ],
            })
          },
        })
        return
      }

      // quantity is being reduced but not to 0, and not below the threshold
      return basketItems.updateQuantity({
        basketItemId: basketItemWithPrices.basketItem.id,
        quantity: newQuantity,
      })
    }

  return (
    <>
      <AlertModal
        isOpen={showModal}
        title={modalData ? modalData.title : ''}
        subTitle={modalData ? modalData.subtitle : ''}
        action={{
          text: t('confirm'),
          intent: ButtonType.DANGER,
          onClick: () => {
            const actionToExecute = modalData?.onModalCloseAfterConfirmAction
            setShowModal(false)
            setTimeout(() => {
              setModalData(null)
              actionToExecute?.()
            }, 300)
          },
        }}
        cancel={{
          text: t('cancel'),
          intent: ButtonType.SECONDARY,
          onClick: () => {
            setShowModal(false)
            setTimeout(() => {
              setModalData(null)
            }, 300)
          },
        }}
        dataTestId="basket-item-modal-button-remove"
      />
      <Container>
        {basketTotalsData.basketItemsWithPrices.map(basketItemWithPrices => (
          <BasketItemComponent
            key={basketItemWithPrices.basketItem.id}
            basketItemWithPrices={basketItemWithPrices}
            allowSingleItemOrderNotes={allowSingleItemOrderNotes}
            effectiveMaxQuantity={getEffectiveMaxQuantity(basketItemWithPrices)}
            updateQuantity={curriedUpdateBasketItemQuantity(
              basketItemWithPrices
            )}
          />
        ))}
      </Container>
    </>
  )
}
